import React, { useState, useEffect } from 'react';
import { useSearchParams, Navigate } from 'react-router-dom';
import { format } from 'date-fns';
import PageTitle from '../components/ui/PageTitle';
import BookingSteps from '../components/booking/BookingSteps';
import BookingCalendar from '../components/booking/BookingCalendar';
import BookingContactForm from '../components/booking/BookingContactForm';
import BookingReview from '../components/booking/BookingReview';
import BookingConfirmation from '../components/booking/BookingConfirmation';
import StoreOfflineBanner from '../components/StoreOfflineBanner';
import useStoreStatus from '../hooks/useStoreStatus';
import { supabase } from '../lib/supabase';

function getSessionId() {
     let id = localStorage.getItem('ripout_session_id');
     if (!id) {
          id = crypto.randomUUID();
          localStorage.setItem('ripout_session_id', id);
     }
     return id;
}

function parsePreselectedItems(raw) {
     if (!raw) return null;
     const parsed = raw
          .split(',')
          .map((pair) => {
               const [id, qty] = pair.split(':');
               const n = Number(qty);
               return id && n > 0 ? { id, qty: n } : null;
          })
          .filter(Boolean);
     return parsed.length ? parsed : null;
}

function buildEditCartHref(equipmentData) {
     if (!equipmentData || equipmentData.mode === 'package') return '/pricing';
     const items = (equipmentData.items || []).filter((i) => !i.is_package);
     if (!items.length) return '/pricing';
     const pairs = items.map((i) => `${i.id}:${i.selectedQty || 1}`).join(',');
     return `/pricing?items=${encodeURIComponent(pairs)}`;
}

export default function Schedule() {
     const [searchParams] = useSearchParams();
     const isSuccess = searchParams.get('success') === 'true';
     const isCancelled = searchParams.get('cancelled') === 'true';
     const preselectedItems = parsePreselectedItems(searchParams.get('items'));
     const { offline, message, loading: statusLoading } = useStoreStatus();
     const mode = searchParams.get('mode') || (preselectedItems ? 'alacarte' : null);
     const deliveryChoice = searchParams.get('delivery') === 'delivery' ? 'delivery' : 'pickup';

     // Step 0 = Dates, 1 = Contact, 2 = Review
     const [step, setStep] = useState(0);
     const [equipmentData, setEquipmentData] = useState(null);
     const [bookingData, setBookingData] = useState(null);
     const [contactData, setContactData] = useState(null);
     const [intentId, setIntentId] = useState(null);
     const [resumeData, setResumeData] = useState(null);
     const [error, setError] = useState(null);
     const [hydrating, setHydrating] = useState(true);

     const sessionId = getSessionId();

     // Hydrate equipmentData from URL preselection (Pricing page handoff)
     useEffect(() => {
          if (isSuccess) {
               setHydrating(false);
               return;
          }
          if (!preselectedItems || !mode) {
               setHydrating(false);
               return;
          }
          (async () => {
               const ids = preselectedItems.map((p) => p.id);
               const { data } = await supabase
                    .from('equipment')
                    .select('*')
                    .in('id', ids);
               if (!data || data.length === 0) {
                    setHydrating(false);
                    return;
               }

               const qtyById = Object.fromEntries(
                    preselectedItems.map((p) => [p.id, p.qty])
               );
               const items = data.map((row) => ({
                    ...row,
                    selectedQty: qtyById[row.id] || 1,
               }));

               const dailyRateCents = items.reduce(
                    (sum, i) => sum + i.daily_rate * i.selectedQty,
                    0
               );

               const deliveryFeeCents = items
                    .filter((i) => i.delivery_available)
                    .reduce(
                         (sum, i) => sum + (i.delivery_fee || 0) * i.selectedQty,
                         0
                    );
               const hasDelivery = deliveryChoice === 'delivery' && deliveryFeeCents > 0;

               const description = items
                    .map((i) =>
                         i.selectedQty > 1 ? `${i.name} x${i.selectedQty}` : i.name
                    )
                    .join(', ');

               const data_ = {
                    mode,
                    items,
                    dailyRateCents,
                    description,
                    delivery: {
                         method: hasDelivery ? 'delivery' : 'pickup',
                         deliveryFeeCents: hasDelivery ? deliveryFeeCents : 0,
                    },
               };

               setEquipmentData(data_);
               await saveIntent(1, {
                    equipment_mode: mode,
                    equipment_items: items,
                    equipment_description: description,
                    daily_rate_cents: dailyRateCents,
                    delivery_method: hasDelivery ? 'delivery' : 'pickup',
               });
               setHydrating(false);
          })();
          // eslint-disable-next-line react-hooks/exhaustive-deps
     }, []);

     // Check for existing intent on mount (only if no URL preselection)
     useEffect(() => {
          if (isSuccess || preselectedItems) return;
          checkForExistingIntent();
          // eslint-disable-next-line react-hooks/exhaustive-deps
     }, []);

     // Mark intent completed on success
     useEffect(() => {
          if (isSuccess) {
               const storedIntentId = localStorage.getItem('ripout_intent_id');
               if (storedIntentId) {
                    supabase
                         .from('booking_intents')
                         .update({ status: 'completed', updated_at: new Date().toISOString() })
                         .eq('id', storedIntentId)
                         .then(() => {
                              localStorage.removeItem('ripout_intent_id');
                         });
               }
          }
     }, [isSuccess]);

     async function checkForExistingIntent() {
          const { data } = await supabase
               .from('booking_intents')
               .select('*')
               .eq('session_id', sessionId)
               .eq('status', 'in_progress')
               .order('updated_at', { ascending: false })
               .limit(1);

          if (data && data.length > 0) {
               const intent = data[0];
               if (intent.current_step > 0) {
                    setResumeData(intent);
               }
          }
     }

     function handleResume() {
          const intent = resumeData;
          setIntentId(intent.id);
          localStorage.setItem('ripout_intent_id', intent.id);

          if (intent.equipment_mode) {
               const items = intent.equipment_items || [];
               const dailyRateCents = intent.daily_rate_cents || 0;
               const deliveryItems = items.filter((i) => i.delivery_available);
               const deliveryFeeCents = deliveryItems.reduce(
                    (sum, i) => sum + ((i.delivery_fee || 0) * (i.selectedQty || 1)),
                    0
               );
               const hasDelivery = intent.delivery_method === 'delivery' && deliveryFeeCents > 0;
               setEquipmentData({
                    mode: intent.equipment_mode,
                    items,
                    dailyRateCents,
                    description: intent.equipment_description,
                    delivery: {
                         method: hasDelivery ? 'delivery' : 'pickup',
                         deliveryFeeCents: hasDelivery ? deliveryFeeCents : 0,
                    },
               });

               if (intent.start_date) {
                    const days = intent.days || 1;
                    const depositCents = items.reduce(
                         (sum, i) => sum + ((i.deposit_amount || 0) * (i.selectedQty || 1)),
                         0
                    );
                    const rentalCents = dailyRateCents * days;
                    const actualDeliveryFee = hasDelivery ? deliveryFeeCents : 0;
                    const subtotalCents = rentalCents + actualDeliveryFee + depositCents;
                    const convenienceFeeCents = Math.round(subtotalCents * 0.032);
                    const totalCents = subtotalCents + convenienceFeeCents;
                    setBookingData({
                         startDate: new Date(intent.start_date),
                         endDate: new Date(intent.end_date),
                         days,
                         rentalCents,
                         deliveryFeeCents: actualDeliveryFee,
                         depositCents,
                         convenienceFeeCents,
                         totalCents,
                    });
               }
          }
          if (intent.customer_name) {
               setContactData({
                    name: intent.customer_name,
                    email: intent.customer_email,
                    phone: intent.customer_phone,
                    notes: intent.notes,
                    address: intent.customer_address || '',
                    city: intent.customer_city || '',
                    state: intent.customer_state || 'AZ',
                    zip: intent.customer_zip || '',
               });
          }

          // Legacy intents had step 0 = equipment. Map to new 0-2 range.
          setStep(Math.max(0, (intent.current_step || 1) - 1));
          setResumeData(null);
     }

     function handleDismissResume() {
          if (resumeData) {
               supabase
                    .from('booking_intents')
                    .update({ status: 'abandoned', updated_at: new Date().toISOString() })
                    .eq('id', resumeData.id);
          }
          setResumeData(null);
     }

     async function saveIntent(stepNum, data) {
          const payload = {
               session_id: sessionId,
               current_step: stepNum,
               updated_at: new Date().toISOString(),
               ...data,
          };

          if (intentId) {
               await supabase
                    .from('booking_intents')
                    .update(payload)
                    .eq('id', intentId);
          } else {
               const { data: newIntent } = await supabase
                    .from('booking_intents')
                    .insert(payload)
                    .select()
                    .single();
               if (newIntent) {
                    setIntentId(newIntent.id);
                    localStorage.setItem('ripout_intent_id', newIntent.id);
               }
          }
     }

     // Step 0: Dates
     function handleDateSelect(data) {
          const deliveryFee = equipmentData?.delivery?.deliveryFeeCents || 0;
          const depositCents = equipmentData?.items?.reduce(
               (sum, i) => sum + ((i.deposit_amount || 0) * (i.selectedQty || 1)),
               0
          ) || 0;
          const rentalCents = equipmentData.dailyRateCents * data.days;
          const subtotalCents = rentalCents + deliveryFee + depositCents;
          const convenienceFeeCents = Math.round(subtotalCents * 0.032);
          const totalCents = subtotalCents + convenienceFeeCents;
          const fullData = {
               ...data,
               rentalCents,
               deliveryFeeCents: deliveryFee,
               depositCents,
               convenienceFeeCents,
               totalCents,
          };
          setBookingData(fullData);
          saveIntent(2, {
               start_date: format(data.startDate, 'yyyy-MM-dd'),
               end_date: format(data.endDate, 'yyyy-MM-dd'),
               days: data.days,
               total_cents: totalCents,
          });
          setStep(1);
     }

     // Step 1: Contact Info
     function handleContactSubmit(data) {
          setContactData(data);
          saveIntent(3, {
               customer_name: data.name,
               customer_email: data.email,
               customer_phone: data.phone,
               notes: data.notes,
               customer_address: data.address,
               customer_city: data.city,
               customer_state: data.state,
               customer_zip: data.zip,
          });
          setStep(2);
     }

     // Step 2: Checkout
     async function handleCheckout(agreement) {
          setError(null);
          // Last gate before money moves: never start checkout while the store
          // is away, or before we know whether it is.
          if (statusLoading || offline) {
               setError(
                    'Booking is temporarily closed. Please check back soon.'
               );
               return;
          }
          try {
               const delivery = equipmentData?.delivery || { method: 'pickup', deliveryFeeCents: 0 };
               const { data, error: fnError } = await supabase.functions.invoke(
                    'create-checkout',
                    {
                         body: {
                              startDate: format(bookingData.startDate, 'yyyy-MM-dd'),
                              endDate: format(bookingData.endDate, 'yyyy-MM-dd'),
                              days: bookingData.days,
                              totalCents: bookingData.totalCents,
                              equipmentMode: equipmentData.mode,
                              agreedToSafety: !!agreement?.agreedToSafety,
                              agreedToRentalTerms: !!agreement?.agreedToRentalTerms,
                              agreementVersion: agreement?.agreementVersion || null,
                              agreementAcceptedAt: agreement?.agreementAcceptedAt || null,
                              equipmentItems: equipmentData.items.map((i) => ({
                                   id: i.id,
                                   name: i.name,
                                   selectedQty: i.selectedQty || 1,
                                   dailyRateCents: i.daily_rate,
                              })),
                              equipmentDescription: equipmentData.description,
                              deliveryMethod: delivery.method,
                              deliveryFeeCents: delivery.deliveryFeeCents,
                              rentalCents: bookingData.rentalCents,
                              depositCents: bookingData.depositCents,
                              convenienceFeeCents: bookingData.convenienceFeeCents,
                              customerName: contactData.name,
                              customerEmail: contactData.email,
                              customerPhone: contactData.phone,
                              notes: contactData.notes,
                              customerAddress: contactData.address,
                              customerCity: contactData.city,
                              customerState: contactData.state,
                              customerZip: contactData.zip,
                              intentId,
                         },
                    }
               );

               if (fnError) throw fnError;
               if (data?.url) {
                    window.location.href = data.url;
               } else {
                    throw new Error('No checkout URL returned');
               }
          } catch (err) {
               setError('Something went wrong creating your checkout session. Please try again.');
               console.error('Checkout error:', err);
          }
     }

     // Confirmation (returning from Stripe)
     if (isSuccess) {
          return (
               <>
                    <PageTitle
                         firstWord='Booking'
                         secondWord='Confirmed'
                         breadcrumbLink='Schedule'
                    />
                    <section className='bg-white py-12'>
                         <div className='container'>
                              <BookingConfirmation />
                         </div>
                    </section>
               </>
          );
     }

     // Redirect to /pricing if no preselection and no resume
     if (!hydrating && !equipmentData && !resumeData) {
          return <Navigate to='/pricing' replace />;
     }

     // Store offline — block the entire booking flow
     if (!statusLoading && offline) {
          return (
               <>
                    <PageTitle
                         firstWord='Schedule'
                         secondWord='Your Rental'
                         breadcrumbLink='Schedule'
                    />
                    <section className='bg-white py-12 laptop:py-20 min-h-[60vh]'>
                         <div className='mx-auto max-w-3xl px-6 laptop:px-8'>
                              <StoreOfflineBanner message={message} />
                              <div className='text-center mt-6'>
                                   <a
                                        href='/pricing'
                                        className='inline-block py-3 px-6 rounded-xl border-2 border-gray-200 text-darker/60 font-semibold hover:border-darker/30 hover:text-darker duration-300'
                                   >
                                        ← Back to Pricing
                                   </a>
                              </div>
                         </div>
                    </section>
               </>
          );
     }

     return (
          <>
               <PageTitle
                    firstWord='Schedule'
                    secondWord='Your Rental'
                    breadcrumbLink='Schedule'
               />
               <section className='bg-white py-12 laptop:py-20 min-h-[60vh]'>
                    <div className='mx-auto max-w-5xl px-6 laptop:px-8'>
                         {/* Resume Banner */}
                         {resumeData && (
                              <div className='bg-yellow/10 border border-yellow rounded-xl p-5 mb-8 flex flex-col tablet:flex-row items-center justify-between gap-4'>
                                   <div>
                                        <p className='text-darker font-semibold'>
                                             Welcome back! You have an unfinished booking.
                                        </p>
                                        <p className='text-darker/60 text-sm'>
                                             {resumeData.equipment_description && (
                                                  <span>{resumeData.equipment_description}</span>
                                             )}
                                             {resumeData.start_date && (
                                                  <span> · {resumeData.start_date} to {resumeData.end_date}</span>
                                             )}
                                        </p>
                                   </div>
                                   <div className='flex gap-2'>
                                        <button
                                             onClick={handleResume}
                                             className='px-4 py-2 rounded-lg text-sm bg-yellow text-dark font-semibold hover:bg-yellow/90 duration-300'
                                        >
                                             Resume
                                        </button>
                                        <button
                                             onClick={handleDismissResume}
                                             className='px-4 py-2 rounded-lg text-sm border border-gray-200 text-darker/60 hover:text-darker hover:border-darker/40 duration-300'
                                        >
                                             Start Over
                                        </button>
                                   </div>
                              </div>
                         )}

                         {isCancelled && (
                              <div className='bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-center text-red-700'>
                                   Payment was cancelled. You can try booking again below.
                              </div>
                         )}

                         {error && (
                              <div className='bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-center text-red-700'>
                                   {error}
                              </div>
                         )}

                         {equipmentData && <BookingSteps currentStep={step} />}

                         {equipmentData && step === 0 && (
                              <BookingCalendar
                                   equipmentData={equipmentData}
                                   onSelect={handleDateSelect}
                                   onBack={null}
                                   editCartHref={buildEditCartHref(equipmentData)}
                                   onChangeDelivery={(method) => {
                                        const feeBase = equipmentData.items
                                             .filter((i) => i.delivery_available)
                                             .reduce(
                                                  (sum, i) => sum + (i.delivery_fee || 0) * (i.selectedQty || 1),
                                                  0
                                             );
                                        const fee = method === 'delivery' ? feeBase : 0;
                                        setEquipmentData({
                                             ...equipmentData,
                                             delivery: { method, deliveryFeeCents: fee },
                                        });
                                        saveIntent(1, { delivery_method: method });
                                   }}
                              />
                         )}
                         {equipmentData && step === 1 && (
                              <BookingContactForm
                                   bookingData={bookingData}
                                   initialData={contactData}
                                   equipmentData={equipmentData}
                                   onSubmit={handleContactSubmit}
                                   onBack={() => setStep(0)}
                              />
                         )}
                         {equipmentData && step === 2 && (
                              <BookingReview
                                   equipmentData={equipmentData}
                                   deliveryData={equipmentData?.delivery}
                                   bookingData={bookingData}
                                   contactData={contactData}
                                   onConfirm={handleCheckout}
                                   onBack={() => setStep(1)}
                              />
                         )}
                    </div>
               </section>
          </>
     );
}
